<script setup lang="ts">
import { onMounted, ref } from 'vue'
import axios from 'axios'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ArrowLeft, FileText, FolderKanban, Loader2, Map, Plus } from 'lucide-vue-next'
import { useToast } from '@/components/ui/toast'
import { useI18n } from 'vue-i18n'
import ProcessMap from './ProcessMap.vue'
import ProcessEditor from './ProcessEditor.vue'
import ProcessMedia from './ProcessMedia.vue'
import ProjectList from './ProjectList.vue'

const { t } = useI18n()
const { toast } = useToast()
const activeTab = ref('map')
const loading = ref(true)
const projects = ref<any[]>([])
const selectedProject = ref<any>(null)
const processes = ref<any[]>([])
const documents = ref<any[]>([])
const processOptions = ref({ users: [] as any[], functions: [] as any[] })
const documentOptions = ref({ users: [] as any[], roles: [] as any[] })
const capabilities = ref({ can_view: false, can_edit: false, can_release: false })
const selectedProcess = ref<any>(null)
const creating = ref(false)
const createForm = ref({ code: '', title: '', category: 'core', owner_user_id: null as number | null })
const tabs = [
  { id: 'map', icon: Map },
  { id: 'processes', icon: FolderKanban },
  { id: 'media', icon: FileText },
]

function userName(user: any) {
  return [user.firstname, user.lastname].filter(Boolean).join(' ') || user.email || `#${user.id}`
}

async function loadProjects() {
  loading.value = true
  try {
    const response = await axios.get('/process_management/projects')
    projects.value = response.data.projects || []
    capabilities.value = response.data.capabilities || capabilities.value
  } catch (error: any) {
    toast({ title: t('processManagement.loadFailed'), description: error.response?.data?.error, variant: 'destructive' })
  } finally { loading.value = false }
}

async function loadAll(showLoading = true) {
  if (!selectedProject.value) return
  if (showLoading) loading.value = true
  try {
    const [processResponse, documentResponse] = await Promise.all([
      axios.get(`/process_management/projects/${selectedProject.value.id}/processes`),
      axios.get(`/process_management/projects/${selectedProject.value.id}/documents`),
    ])
    processes.value = processResponse.data.processes || []
    documents.value = documentResponse.data.documents || []
    processOptions.value = processResponse.data.options || { users: [], functions: [] }
    documentOptions.value = documentResponse.data.options || { users: [], roles: [] }
    capabilities.value = processResponse.data.capabilities
  } catch (error: any) {
    toast({ title: t('processManagement.loadFailed'), description: error.response?.data?.error, variant: 'destructive' })
  } finally {
    if (showLoading) loading.value = false
  }
}

onMounted(loadProjects)

async function openProject(project: any) {
  selectedProject.value = project
  selectedProcess.value = null
  activeTab.value = 'map'
  await loadAll()
}

function closeProject() {
  selectedProject.value = null
  selectedProcess.value = null
  processes.value = []
  documents.value = []
  activeTab.value = 'map'
}

async function createProject(project: any) {
  try {
    await axios.post('/process_management/projects', { project })
    await loadProjects()
  } catch (error: any) {
    toast({ title: t('processManagement.saveFailed'), description: error.response?.data?.error, variant: 'destructive' })
  }
}

async function updateProject(project: any) {
  try {
    await axios.patch(`/process_management/projects/${project.id}`, { project: { name: project.name, description: project.description, position: project.position, lock_version: project.lock_version } })
    await loadProjects()
  } catch (error: any) {
    toast({ title: t('processManagement.saveFailed'), description: error.response?.data?.error, variant: 'destructive' })
  }
}

async function archiveProject(project: any) {
  try {
    await axios.post(`/process_management/projects/${project.id}/archive`, { lock_version: project.lock_version })
    if (selectedProject.value?.id === project.id) closeProject()
    await loadProjects()
  } catch (error: any) {
    toast({ title: t('processManagement.saveFailed'), description: error.response?.data?.error, variant: 'destructive' })
  }
}

async function openProcess(id: number) {
  const response = await axios.get(`/process_management/processes/${id}`)
  selectedProcess.value = response.data.process
  activeTab.value = 'processes'
}

async function createProcess() {
  try {
    const response = await axios.post(`/process_management/projects/${selectedProject.value.id}/processes`, { process: createForm.value })
    creating.value = false
    createForm.value = { code: '', title: '', category: 'core', owner_user_id: null }
    await loadAll()
    await openProcess(response.data.process.id)
  } catch (error: any) {
    toast({ title: t('processManagement.saveFailed'), description: error.response?.data?.error, variant: 'destructive' })
  }
}

async function processUpdated(process: any) {
  selectedProcess.value = process
  // Refresh navigation data without unmounting the editor and losing its
  // active tab or an open linked-process sheet.
  await loadAll(false)
}

async function processArchived() {
  selectedProcess.value = null
  await loadAll()
}

function selectTab(tab: string) {
  activeTab.value = tab
  if (tab !== 'processes') selectedProcess.value = null
}
</script>

<template>
  <div class="process-management min-h-screen bg-slate-50 p-4 md:p-6">
    <div class="mx-auto max-w-[1500px] space-y-5">
      <header class="flex flex-wrap items-center justify-between gap-4 rounded-xl border bg-white px-5 py-4 shadow-sm">
        <div class="flex items-center gap-3">
          <Button v-if="selectedProject" size="icon" variant="outline" :title="t('processManagement.backToProjects')" @click="closeProject"><ArrowLeft class="h-4 w-4" /></Button>
          <div><h1 class="text-2xl font-semibold text-slate-900">{{ selectedProject?.name || t('processManagement.title') }}</h1><p class="text-sm text-slate-500">{{ selectedProject?.description || t('processManagement.subtitle') }}</p></div>
        </div>
        <Button v-if="capabilities.can_edit && activeTab === 'processes' && !selectedProcess" class="bg-[#80BA27] text-white hover:bg-[#70a522]" @click="creating = !creating"><Plus class="mr-2 h-4 w-4" />{{ t('processManagement.newProcess') }}</Button>
      </header>

      <nav v-if="selectedProject" class="grid grid-cols-1 gap-2 rounded-xl border bg-white p-2 shadow-sm sm:grid-cols-3">
        <button v-for="tab in tabs" :key="tab.id" class="flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition" :class="activeTab === tab.id ? 'bg-[#80BA27] text-white' : 'text-slate-600 hover:bg-slate-100'" @click="selectTab(tab.id)">
          <component :is="tab.icon" class="h-4 w-4" />{{ t(`processManagement.nav.${tab.id}`) }}
        </button>
      </nav>

      <div v-if="loading" class="flex items-center justify-center rounded-xl border bg-white p-16 text-slate-500"><Loader2 class="mr-2 h-5 w-5 animate-spin" />{{ t('processManagement.loading') }}</div>

      <template v-else-if="!selectedProject">
        <ProjectList :projects="projects" :capabilities="capabilities" @open="openProject" @create="createProject" @update="updateProject" @archive="archiveProject" />
      </template>

      <template v-else>
        <ProcessMap v-if="activeTab === 'map'" :processes="processes" @open="openProcess" />

        <section v-if="activeTab === 'processes' && !selectedProcess" class="overflow-hidden rounded-xl border bg-white shadow-sm">
          <form v-if="creating" class="grid gap-3 border-b bg-slate-50 p-4 md:grid-cols-4" @submit.prevent="createProcess">
            <div><Label>{{ t('processManagement.code') }}</Label><Input v-model="createForm.code" required /></div>
            <div><Label>{{ t('processManagement.processTitle') }}</Label><Input v-model="createForm.title" required /></div>
            <div><Label>{{ t('processManagement.category') }}</Label><select v-model="createForm.category" class="h-10 w-full rounded-md border bg-white px-3"><option v-for="category in ['management','core','support']" :key="category" :value="category">{{ t(`processManagement.categories.${category}`) }}</option></select></div>
            <div><Label>{{ t('processManagement.owner') }}</Label><select v-model="createForm.owner_user_id" class="h-10 w-full rounded-md border bg-white px-3"><option :value="null">—</option><option v-for="user in processOptions.users" :key="user.id" :value="user.id">{{ userName(user) }}</option></select></div>
            <div class="md:col-span-4"><Button type="submit">{{ t('processManagement.create') }}</Button></div>
          </form>
          <button v-for="process in processes" :key="process.id" class="grid w-full gap-2 border-b p-4 text-left hover:bg-slate-50 sm:grid-cols-[120px_minmax(0,1fr)_180px] sm:items-center" @click="openProcess(process.id)"><span class="text-sm font-semibold text-slate-600">{{ process.code }}</span><span class="font-medium">{{ process.title }}</span><span class="text-sm text-slate-500">{{ t(`processManagement.categories.${process.category}`) }}</span></button>
          <p v-if="!processes.length" class="p-10 text-center text-slate-500">{{ t('processManagement.noProcesses') }}</p>
        </section>

        <ProcessEditor v-if="activeTab === 'processes' && selectedProcess" :process="selectedProcess" :processes="processes" :documents="documents" :options="processOptions" :capabilities="capabilities" @back="selectedProcess = null" @updated="processUpdated" @archived="processArchived" @open-process="openProcess" />
        <ProcessMedia v-if="activeTab === 'media'" :project="selectedProject" :documents="documents" :processes="processes" :options="documentOptions" :capabilities="capabilities" @reload="loadAll" />
      </template>
    </div>
  </div>
</template>

<style scoped>
.process-management :deep(.bg-primary) {
  background-color: #80ba27;
}

.process-management :deep(.bg-primary:hover) {
  background-color: #9cd33d;
}
</style>
